<template>
    <div class="p-relative">
        <Toolbar
            :is-list="true"
            v-on:toolbar-button-click="onToolbarClick"
        >
            <SearchPanel
                v-model="search"
                :has-filter="false"
            />
        </Toolbar>

        <FilterPanel
            v-if="filterDataSource"
            :dataSource="filterDataSource"
            v-bind:status.sync="period"
        >
            <ExtendedFilterPanel
                v-if="period == 'Free' && year && month"
                v-bind:value.sync="extendedFilter"
            />
        </FilterPanel>


        <v-data-table
            :loading="loading"
            :headers="headers"
            :items="entities"
            :options.sync="options"
            :server-items-length="total"
            :items-per-page="10"
            :footer-props="{ itemsPerPageOptions: itemsPerPageSettings }"
            :page.sync="options.page"
            @page-count="paginationPageCount = $event"
            class="elevation-1 customization-list-table pagination-list-table sticky-table"
            @dblclick:row="(event, data) => $eventBus.$emit('open-document', { id: data.item.id, type: 'Chancellery|Documents.Document', common: data.item.iscommon })"
        >
            <v-progress-linear slot="progress" height="2" color="#E43958" indeterminate></v-progress-linear>

            <template v-slot:[`item.controldate`]="{ item }">
                <div class="d-flex align-center">
                    {{ getSrokFieldText(item) }}
                    <template v-if="item.prolongationcount > 0">
                        &nbsp;
                        <div class="font-weight-bold" style="color: #1976d2">({{ toRoman(item.prolongationcount) }})</div>
                    </template>
                </div>
            </template>

            <template v-slot:[`item.mainregdate`]="{ item }">
                <div>{{dateFormat(item.mainregdate, 'DD.MM.YYYY')}}</div>
            </template>

            <template v-slot:[`item.authorname`]="{ item }">
                <v-workplace-chip
                    v-if="item.authorname"
                    :id="item.authorid"
                    :name="item.authorname"
                />
            </template>

            <template v-slot:[`item.maintheme`]="{ item }">
                <div class="elipsis-text-wrap">
                    <div class="etw-text-container">
                        <div class="etw-text" v-tooltip.top-center="item.maintheme">
                            {{ item.maintheme }}
                        </div>
                    </div>
                </div>
            </template>

            <template v-slot:[`item.ordersubject`]="{ item }">
                <div class="elipsis-text-wrap">
                    <div class="etw-text-container">
                        <div class="etw-text" v-tooltip.top-center="item.ordersubject">
                            {{ item.ordersubject }}
                        </div>
                    </div>
                </div>
            </template>

            <template v-slot:[`item.svod`]="{ item }">
                <v-workplace-chip
                    v-if="item.svod"
                    :id="item.svodworkplaceid"
                    :name="item.svod"
                />
            </template>

            <template v-slot:[`item.controlstatus`]="{ item }">
                <StatusChip :status="getStatus(item)"/>
            </template>

            <template v-slot:[`footer.prepend`]>
                <v-pagination
                    v-model="options.page"
                    :length="paginationPageCount"
                    :total-visible="$listPaginationTotal"
                    color="teal"
                ></v-pagination>
            </template>

        </v-data-table>

        <SelectMonthYearDlg ref="SelectMonthYearDlgRef"/>
    </div>
</template>

<script>
import i18n from '@/i18n';
import { httpAPI } from "@/api/httpAPI";
import _ from 'lodash';
import sys from '@/services/system';
import { mapActions, mapGetters } from 'vuex';
import Toolbar from '@/components/documents/Toolbar.vue';
import SearchPanel from '@/components/documents/SearchPanel.vue';
import StatusChip from '@/components/global/chips/StatusChip';
import FilterPanel from '@/components/documents/FilterPanel.vue';
import SelectMonthYearDlg from '@/components/global/dialogs/SelectMonthYearDlg.vue';
import ExtendedFilterPanel from '@/components/documents/ExtendedFilterPanel.vue';

export default {
    name: "OrdersSvetoforByPeriod",
    components: {
        Toolbar,
        SearchPanel,
        StatusChip,
        FilterPanel,
        SelectMonthYearDlg,
        ExtendedFilterPanel
    },
    columnIndex: {
        "regnumber" : 5,
        "controldate": 8,
        "authorname" : 10,
        "svod": 11,
        "executiontext": 13,
        "mainregdate": 17,
        "maintheme": 18,
        "ordersubject": 24,
    },
    computed:{
        ...mapGetters({ itemsPerPageSettings: 'getItemsPerPageSettings' }),
        headers() {
            let arr =  [
                { text: i18n.t("№_пункта"), value: 'regnumber', width: '150px' },
                { text: i18n.t("Рег._дата"), value: 'mainregdate', width: '110px' },
                { text: i18n.t("Автор_поручения"), value: 'authorname', width: '15%' },
                { text: i18n.t("Протокол"), value: 'maintheme' },
                { text: i18n.t("Поручение"), value: 'ordersubject' },
                { text: i18n.t("Срок_исполнения"), value: 'controldate', width: '220px' },
                { text: i18n.t("Исполнитель"), value: 'svod'},
                { text: i18n.t("Статус"), value: 'controlstatus', width: '150px', sortable: false, class: 'status-col' }
            ]
            return arr;
        },
        search: {
            get: function() {
                return this.$store.getters[`orders/lists/protocolsbycurrentperiod/getSearch`];
            },
            set: _.debounce(function(v) {
                this.$store.commit(`orders/lists/protocolsbycurrentperiod/SET_TABLE_OPTION_PAGE`, 1);
                this.$store.commit(`orders/lists/protocolsbycurrentperiod/SET_SEARCH`, v);
            }, 500)
        },
        period: {
            get: function() {
                return this.$store.getters[`orders/lists/protocolsbycurrentperiod/getPeriod`];
            },
            set: function(v) {
                this.$store.commit(`orders/lists/protocolsbycurrentperiod/SET_TABLE_OPTION_PAGE`, 1);
                this.$store.commit(`orders/lists/protocolsbycurrentperiod/SET_PERIOD`, v);
            }
        },
        month: {
            get: function() {
                return this.$store.getters[`orders/lists/protocolsbycurrentperiod/getMonth`];
            },
            set: function(v) {
                this.$store.commit(`orders/lists/protocolsbycurrentperiod/SET_MONTH`, v);
            }
        },
        year: {
            get: function() {
                return this.$store.getters[`orders/lists/protocolsbycurrentperiod/getYear`];
            },
            set: function(v) {
                this.$store.commit(`orders/lists/protocolsbycurrentperiod/SET_YEAR`, v);
            }
        },
        options: {
            get: function() {
                return this.$store.getters[`orders/lists/protocolsbycurrentperiod/getTableOptions`];
            },
            set: function(newValue) {
                this.$store.commit(`orders/lists/protocolsbycurrentperiod/SET_TABLE_OPTION`, newValue);
            }
        },
        extendedFilter: {
            get: function() {
                return { Year : { label: i18n.t("Год"), value: this.year, notdel: true}, Month : { label: i18n.t("Месяц"), value: i18n.t(this.month.name), notdel: true} }
            },
            set: function() {
                this.period = "Current";
                this.year = null;
                this.month = null;
                this.getData();
            }
        },
        filter () {
            return {
                ...this.options,
                ArchYear: -1,
                SearchStr: this.search,
                Period: this.period
                //ProtocolControlState: '1',//  '1' - В работе '2' - Выполнен '3' - Снят с контроля
                //ExecutionStatus: this.status == 4 ? 'Expired' : 'All' //'Expired' - просроченные
            }
        },
        avaibleStatuses() {
            return this.$store.getters['documents/references/GetAvaibleStatuses'];
        },
    },
    data: () => {
        return {
            loading: true,
            total: 0,            
            entities: [],
            prevTableFilterObject: null,
            paginationPageCount: 0,
            filterDataSource: null,
        }
    },
    methods: {
        ...mapActions('orders', ['updateCounter']),
        getStatus(item){
            return this.avaibleStatuses[item.controlstatus] ?? item.controlstatus;
        },
        onToolbarClick(event, btn){
            this[btn.Action](btn);
        },
        async Refresh() {
            await this.getData(true);
        },
        async Export(){
            let filter = JSON.parse(this.prevTableFilterObject);
            delete filter.Limit;
            delete filter.Offset;
            let exportURI = `api/orders/svetoforexport_v2?param=${JSON.stringify(filter)}&mode=${process.env.NODE_ENV}`;
            exportURI += `&token=${this.$store.getters['global/auth/getUserInfo']?.Token}`;
            
            let text = i18n.t('Экспорт_списка_поручений_от_formatedDateTimeFrom_до_formatedDateTimeTo.message', { formatedDateTimeFrom: this.$moment(filter.From).format('DD.MM.YYYY'), formatedDateTimeTo: this.$moment(filter.To).format('DD.MM.YYYY') });

            this.$eventBus.$emit('on-get-report', {url: exportURI, text: text, type: 'Xls' });
        },
        update: _.debounce(function (object) {            
            this.updateSource(object);
        }, 250),
        async getData(isRefresh = false) {
            this.updateCounter();
            const newTableFilter = await this.getTableFilterObject();
            if (!newTableFilter || (isRefresh === false && this.$_.isEqual(this.prevTableFilterObject, newTableFilter)))
                return;
                
            this.prevTableFilterObject = newTableFilter;
            this.total = 0;
            this.entities = [];
            this.loading = true;

            let response = await httpAPI({
                url: `/api/orders/list?collection=Protocol.Svetofor.V2&filter=${newTableFilter}`,
                method: 'GET'
            });

            this.entities = response?.data?.Payload?.data ?? [];
            this.total = response?.data?.Payload?.total ?? 0;
            this.loading = false;
        },
        async getTableFilterObject() {
            let { sortBy, sortDesc, page, itemsPerPage, SearchStr, Period, ArchYear } = this.filter;
            let OrderDir = sortDesc.length <= 0 ? false : sortDesc[0];
            let sortColumn = sortBy.length <= 0 ? "controldate" : sortBy[0];
            let OrderBy = this.$options.columnIndex[sortColumn];
            let From = null;
            let To = null;
            switch (Period){
                case "Current":
                    From = this.$moment().startOf('month').format('YYYY-MM-DD');
                    To = this.$moment().endOf('month').format('YYYY-MM-DD');
                    break;
                case "Last":
                    From = this.$moment().add(-1, 'month').startOf('month').format('YYYY-MM-DD');
                    To = this.$moment().add(-1, 'month').endOf('month').format('YYYY-MM-DD');
                    break;
                case "Free":
                    if (!this.year || !this.month)
                    {
                        try {
                            await this.selectPeriod();
                        } catch (err) {
                            console.log(err);
                            if (!this.prevTableFilterObject)
                                this.period = "Current";
                            else {
                                const prevPeriod = JSON.parse(this.prevTableFilterObject)?.Period;
                                if (!prevPeriod || prevPeriod == "Free")
                                    this.period = "Current";
                                else 
                                    this.period = prevPeriod;
                            }
                            this.$nextTick(() => {
                                if (document.activeElement) {
                                    document.activeElement.blur();
                                }
                            });
                            return null;
                        }
                    }
                    From = this.$moment({ year: this.year, month: this.month.id - 1 }).startOf('month').format('YYYY-MM-DD');
                    To = this.$moment({ year: this.year, month: this.month.id - 1 }).endOf('month').format('YYYY-MM-DD');
                    break;
            }

            let filter = { /*ProtocolControlState: '1',*/ ExecutionStatus: 'All', Period, From, To, ArchYear, SearchStr, OrderBy, OrderDir, Limit: itemsPerPage, Offset: (page - 1) * itemsPerPage };

            return JSON.stringify(filter);
        },
        async selectPeriod() {
            const selected = await this.$refs.SelectMonthYearDlgRef.open();
            if (selected){
                this.year = selected.year;
                this.month = selected.month;
            }
        },
        getSrokFieldText(item) {
            if (item.prolongationcount)
                return `${this.dateFormat(item.firstcontroldate, 'DD.MM.YYYY')} ➜ ${this.dateFormat(item.controldate, 'DD.MM.YYYY')}`
            return this.dateFormat(item.controldate, 'DD.MM.YYYY')
        },
        async loadFilters() {
            let filterResponse = await httpAPI({
                url: `api/orders/filter?collection=${this.$store.getters['orders/getCurrentCollection']}`,
                method: 'GET',
            });

            this.filterDataSource = filterResponse ? filterResponse.data.Payload : null;
        },
        toRoman: sys.toRoman,
        dateFormat: sys.dateFormat,
    },
    watch: {
        filter: {
            handler () {
                this.getData()
            },
            deep: true,
        },
    },
    async created() {
        let menu = [
            {
                "Action": "Export",
                "Icon": "doc_send",
                "Items": null,
                "Text": "Экспорт_списка",
                "ToolTip": "Экспорт_полного_списка_поручений",
                "Type": "Documents",
                Localize: true
            },
            {
                "Action": "Refresh",
                "Icon": "refresh",
                "Items": null,
                "Text": "Обновить",
                "ToolTip": "Обновить_форму",
                "Type": null,
                Localize: true
            }
        ];
        
        this.$store.commit('documents/toolbar/SET_MENU', { menu });
        this.options.itemsPerPage = this.itemsPerPageSettings[0];
        this.loadFilters();
    },
}
</script>
